import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { cn } from "@/shared/lib";

import { relativeChange, share } from "../../model/change";
import type { DashboardFunnel, DashboardTotals } from "../../model/TeamDashboard";

type Tile = {
  label: string;
  value: string;
  // Изменение со стрелкой; `good` — рост к лучшему (зелёный), `bad` — к худшему.
  delta?: { text: string; tone: "good" | "bad" | "neutral" };
  note: ReactNode;
};

type KpiTilesProps = {
  totals: DashboardTotals;
  funnel: DashboardFunnel;
};

// Главные цифры периода по макету «14б»: посетители, новые игроки, активные сегодня (к вчера
// на тот же час) и за период, вернувшиеся на следующий день, разборы, программа «Матчи
// сразу» и дошедшие до лимита разборов. Рядом — изменение к прошлому такому же периоду.
export const KpiTiles = ({ totals, funnel }: KpiTilesProps) => {
  const { i18n, t } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const percent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const signedPercent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 0,
    signDisplay: "exceptZero",
  });
  const signed = new Intl.NumberFormat(i18n.locale, { signDisplay: "exceptZero" });

  // Изменение в штуках — для маленьких чисел, где проценты скачут.
  const countChange = (current: number, previous: number): Tile["delta"] => {
    const difference = current - previous;
    const text = signed.format(difference);
    return {
      text: t`${text} vs previous period`,
      tone: difference > 0 ? "good" : difference < 0 ? "bad" : "neutral",
    };
  };

  const change = (current: number, previous: number | null): Tile["delta"] => {
    const value = relativeChange(current, previous);
    if (value === null) {
      return undefined;
    }
    const text = signedPercent.format(value);
    return {
      text: t`${text} vs previous period`,
      tone: value > 0 ? "good" : value < 0 ? "bad" : "neutral",
    };
  };

  const visitors = totals.visitors;
  const signedIn = share(totals.new_players, visitors);
  // Сегодня ещё не кончилось: сравниваем со вчера к этому же часу; без времени у вчерашних
  // отметок — просто вчерашнее число, без цвета.
  const sameTime = totals.active_yesterday_same_time;
  const todayChange = totals.active_today - (sameTime ?? 0);
  const yesterdayTotal = number.format(totals.active_yesterday);
  const returnedNextDay = share(funnel.returned_next_day, funnel.returned_next_day_eligible);
  const returned = share(funnel.returned, funnel.returned_eligible);
  const failed = share(totals.analyses_failed, totals.analyses) ?? 0;
  const eligible = funnel.returned_eligible;
  const failedShare = percent.format(failed);
  const signedInShare = signedIn === null ? null : percent.format(signedIn);
  const todayDelta = signed.format(todayChange);
  const weekReturn = returned === null ? "—" : percent.format(returned);
  const ingestActive = number.format(totals.ingest_active);
  const limitAttempts = number.format(totals.limit_attempts);

  const tiles: Tile[] = [
    {
      label: t`Site visitors`,
      value: visitors === null ? "—" : number.format(visitors),
      delta: visitors === null ? undefined : change(visitors, totals.visitors_previous),
      note: visitors === null ? t`Umami is not connected` : t`Umami, unique`,
    },
    {
      label: t`New players`,
      value: number.format(totals.new_players),
      delta: change(totals.new_players, totals.new_players_previous),
      note:
        signedInShare === null
          ? t`First sign-in with Steam`
          : t`${signedInShare} of visitors signed in`,
    },
    {
      label: t`Active today`,
      value: number.format(totals.active_today),
      delta:
        sameTime === null
          ? { text: t`${yesterdayTotal} yesterday`, tone: "neutral" }
          : {
              text: t`${todayDelta} vs yesterday by this hour`,
              tone: todayChange > 0 ? "good" : todayChange < 0 ? "bad" : "neutral",
            },
      note: t`Used Relock after signing in`,
    },
    {
      label: t`Active in the period`,
      value: number.format(totals.active),
      delta: change(totals.active, totals.active_previous),
      note: t`Used it at least once`,
    },
    {
      label: t`Came back the next day`,
      value: returnedNextDay === null ? "—" : percent.format(returnedNextDay),
      note:
        eligible === 0
          ? t`A week later — no one joined 7+ days ago yet`
          : t`A week later — ${weekReturn}`,
    },
    {
      label: t`Analyses`,
      value: number.format(totals.analyses),
      delta: {
        text: t`${failedShare} failed`,
        tone: failed > 0.05 ? "bad" : "neutral",
      },
      note: t`Started in the period`,
    },
    {
      label: t`Installed the app`,
      value: number.format(totals.ingest_connected),
      delta: countChange(totals.ingest_connected, totals.ingest_connected_previous),
      note: t`Deadlock API Ingest, sending matches now: ${ingestActive}`,
    },
    {
      label: t`Reached the limit`,
      value: number.format(totals.limit_reached),
      delta: countChange(totals.limit_reached, totals.limit_reached_previous),
      note: t`Free analyses per day used up; tries over the limit: ${limitAttempts}`,
    },
  ];

  return (
    // Восемь плиток — два ровных ряда по четыре, уже ~740 px (телефон) — по две. Ширину смотрит
    // у обёртки `@container`, а не у окна; заглушка загрузки в `DashboardPage` — так же.
    <section aria-label={t`Key numbers`} className="@container">
      <div className="grid grid-cols-2 gap-3 @min-[46rem]:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="flex min-h-[116px] flex-col gap-0.5 rounded-xl border border-border bg-surface px-4 py-3.5"
          >
            <span className="text-[13px] text-muted">{tile.label}</span>
            <span className="font-mono text-[26px] leading-tight font-semibold">{tile.value}</span>
            {tile.delta !== undefined && (
              <span
                className={cn(
                  "text-[13px] font-semibold",
                  tile.delta.tone === "good" && "text-good",
                  tile.delta.tone === "bad" && "text-bad",
                  tile.delta.tone === "neutral" && "text-muted",
                )}
              >
                {tile.delta.text}
              </span>
            )}
            <span className="text-xs text-subtle">{tile.note}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
